/**
 * @fileoverview Componente átomo VideoMC para reproducción de videos con soporte multi-plataforma y gestión avanzada de estados de carga, error y optimización de rendimiento en el ecosistema Mercately.
 * @module VideoMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import LoadingMC from "./LoadingMC";
import useMediaData from "../hooks/shared/data/useMediaData";
import useMediaUI from "../hooks/shared/ui/useMediaUI";

/**
 * @typedef {Object} VideoData
 * @description Estructura de datos que contiene la información del video y metadatos relacionados
 * @property {string} id - Identificador único del video
 * @property {string} [contentMediaUrl] - URL principal del contenido de video
 * @property {string} [mediaUrl] - URL alternativa del video
 * @property {string} [url] - URL genérica del video
 * @property {string} [type] - Tipo de contenido multimedia
 * @property {Object} [metadata] - Metadatos adicionales del video
 * @property {string} [platform] - Plataforma origen del video (whatsapp, messenger, instagram)
 */

/**
 * @typedef {'whatsapp'|'messenger'|'instagram'|'generic'} PlatformType
 * @description Tipos de plataforma soportados para reproducción de video
 */

/**
 * Componente Átomo para reproducción de videos con soporte multi-plataforma.
 * 
 * Implementa un reproductor de video optimizado con gestión avanzada de estados,
 * soporte para múltiples plataformas de mensajería y manejo robusto de errores.
 * 
 * @component VideoMC
 * @category Component - Atom
 * 
 * @description
 * **Características:**
 * - Reproducción de video con controles nativos del navegador
 * - Gestión de estados de carga y error
 * - Soporte multi-plataforma (WhatsApp, Messenger, Instagram)
 * - Optimización de rendimiento con React.memo
 * - Integración con sistema de traducción i18n
 * - Manejo dinámico de URLs de video
 * 
 * **Atomic Design:**
 * - **Nivel**: Atom
 * - **Composición**: Elemento video nativo con wrapper de estilos
 * - **Dependencias**: LoadingMC (atom), useMediaData (hook), useMediaUI (hook)
 * 
 * **Arquitectura de Hooks:**
 * - useMediaData: Data Layer para gestión de URL y estados de carga
 * - useMediaUI: UI Layer para lógica de interfaz y manejo de errores
 * - useTranslation: Internacionalización de mensajes
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   platform: PlatformType,           // Plataforma origen del video
 *   data: VideoData,                  // Datos del video
 *   className?: string,               // Clases CSS adicionales
 *   urlField?: string                 // Campo específico para URL del video
 * }
 * ```
 * 
 * **Flujo de Renderizado:**
 * 1. **Inicialización**: Carga de datos de video y configuración de hooks
 * 2. **Estado de Carga**: Muestra LoadingMC mientras se cargan los datos
 * 3. **Manejo de Errores**: Presenta mensaje de error si el video no está disponible
 * 4. **Reproducción**: Renderiza elemento video con controles nativos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {PlatformType} props.platform - Plataforma de origen del video (whatsapp, messenger, instagram, generic)
 * @param {VideoData} props.data - Objeto con datos del video y metadatos relacionados
 * @param {string} [props.className=''] - Clases CSS adicionales para personalizar estilos del video
 * @param {string} [props.urlField='contentMediaUrl'] - Campo específico del objeto data que contiene la URL del video
 * 
 * @returns {JSX.Element} Elemento JSX con reproductor de video optimizado
 * 
 * @example
 * ```jsx
 * // Uso básico con datos de WhatsApp
 * <VideoMC 
 *   platform="whatsapp" 
 *   data={{
 *     id: "video123",
 *     contentMediaUrl: "https://example.com/video.mp4"
 *   }}
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso avanzado con campo personalizado y estilos
 * <VideoMC 
 *   platform="messenger" 
 *   data={{
 *     id: "video456",
 *     customVideoUrl: "https://example.com/custom-video.mp4",
 *     metadata: { duration: 120, quality: "HD" }
 *   }}
 *   className="tw-w-full tw-rounded-lg"
 *   urlField="customVideoUrl"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Manejo de estados de error
 * <VideoMC 
 *   platform="instagram" 
 *   data={{
 *     id: "video789",
 *     contentMediaUrl: null // URL inválida para demostrar manejo de errores
 *   }}
 * />
 * ```
 * 
 * @see {@link LoadingMC} Componente de loading usado durante la carga
 * @see {@link useMediaData} Hook para gestión de datos de video
 * @see {@link useMediaUI} Hook para lógica de interfaz de video
 * 
 * @since 1.0.0
 * @version 3.1.0 - Optimización de rendimiento y mejoras en manejo de errores
 */
const VideoMC = memo(({ platform, data, className = '', urlField = 'contentMediaUrl' }) => {
  /**
   * Hook de internacionalización para obtener traducciones específicas del componente.
   * 
   * @constant {Function} t
   * @memberof VideoMC
   * @description Función de traducción del namespace 'InboxMC' para mensajes de error y estados
   */
  const { t } = useTranslation('InboxMC');
  
  /**
   * Hook de Data Layer para gestión de URL y estados de carga del video.
   * 
   * Maneja la extracción y validación de la URL del video desde los datos proporcionados,
   * junto con los estados de carga y error asociados.
   * 
   * @constant {Object} mediaDataState
   * @memberof VideoMC
   * @property {string|null} url - URL procesada del video
   * @property {Function} setUrl - Función para actualizar la URL del video
   * @property {boolean} loading - Estado de carga del video
   * @property {string|null} error - Mensaje de error si ocurre algún problema
   * @property {Function} setLoading - Función para actualizar el estado de carga
   * @property {Function} setError - Función para actualizar el estado de error
   */
  const { url, setUrl, loading, error, setLoading, setError } = useMediaData({ data, urlField });

  /**
   * Hook de UI Layer para lógica de interfaz y manejo de errores del video.
   * 
   * Proporciona funcionalidades específicas de la interfaz de usuario como
   * manejo de errores de reproducción, generación de claves únicas y
   * procesamiento de fuentes de video.
   * 
   * @constant {Object} mediaUIState
   * @memberof VideoMC
   * @property {Function} handleMediaError - Manejador de errores de reproducción de video
   * @property {string} mediaKey - Clave única para forzar re-renderizado del elemento video
   * @property {string} mediaSrc - URL procesada y validada para el elemento source del video
   */
  const {
    handleMediaError,
    mediaKey,
    mediaSrc
  } = useMediaUI({
    platform,
    url,
    data,
    setUrl,
    setError,
    setLoading,
    mediaType: 'video',
  });

  /**
   * Renderizado condicional del componente de video.
   * 
   * Implementa una estrategia de renderizado basada en estados que maneja:
   * - Estado de carga con componente LoadingMC
   * - Estado de error con mensaje internacionalizado
   * - Estado exitoso con reproductor de video nativo
   * 
   * **Estructura de Renderizado:**
   * 1. **Wrapper Principal**: Contenedor con clase tw-wrapper para estilos de Tailwind
   * 2. **Loading State**: LoadingMC visible durante la carga de datos
   * 3. **Error State**: Mensaje de error cuando el video no está disponible
   * 4. **Video Element**: Reproductor HTML5 con controles nativos
   * 
   * **Optimizaciones de Rendimiento:**
   * - Uso de key dinámico para forzar re-renderizado cuando cambia la fuente
   * - Ocultación condicional del video durante la carga
   * - Manejo asíncrono de eventos de carga y error
   * 
   * **Accesibilidad:**
   * - Controles nativos del navegador para accesibilidad completa
   * - Mensaje de error descriptivo para lectores de pantalla
   * - Estructura semántica HTML5
   * 
   * @returns {JSX.Element} Elemento JSX con reproductor de video optimizado
   */
  return (
    <div className="tw-wrapper">
      <LoadingMC loading={loading} />
      {error ? (
        <blockquote className="tw-text-center tw-mt-20 tw-text-xs">
          {t('video.not_available')}
        </blockquote>
      ) : (
        <video
          controls
          key={mediaKey}
          onError={handleMediaError}
          onLoadedData={() => setLoading(false)}
          className={`${className} ${loading ? 'tw-hidden' : ''}`}
        >
          <source src={mediaSrc} />
        </video>
      )}
    </div>
  );
});

/**
 * Nombre para display en herramientas de desarrollo.
 * 
 * Facilita la identificación del componente en React DevTools y herramientas
 * de debugging durante el desarrollo.
 * 
 * @memberof VideoMC
 * @type {string}
 */
VideoMC.displayName = "VideoMC";

export default VideoMC;